import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Chip, CircularProgress, Typography } from '@mui/material'
import type { FC } from 'react'
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom'
import { useWoodworkProject } from '../api-hooks/useWoodworkProjects'
import { formatProjectDateRange } from '../content/formatProjectDate'
import MaterialChips from '../components/MaterialChips'
import PageShell from '../components/PageShell'
import WorkProjectGallery from './WorkProjectGallery'
import WoodworkLogo from './WoodworkLogo'
import './WorkProject.css'
import './WoodworkLogo.css'

const WorkProject: FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const { project, statusLabel, isLoading, notFound } = useWoodworkProject(projectId)
  const dateRange = project
    ? formatProjectDateRange(project.startDate, project.endDate)
    : undefined

  if (isLoading && !project) {
    return (
      <PageShell>
        <CircularProgress size={28} sx={{ color: 'primary.main' }} />
      </PageShell>
    )
  }

  if (notFound || !project) {
    return <Navigate to='/work' replace />
  }

  return (
    <PageShell maxWidth='lg' className='page work-project-page'>
      <WoodworkLogo
        variant='branch'
        size='watermark'
        className='work-page-watermark'
        aria-hidden
      />
      <RouterLink to='/work' className='work-project-back'>
        <ArrowBackIcon fontSize='small' aria-hidden />
        All woodworking
      </RouterLink>

      <header className='work-project-header'>
        <WoodworkLogo variant='branch' size='hero' />
        <div className='work-project-meta'>
          <Chip
            label={statusLabel[project.status]}
            size='small'
            sx={{
              textTransform: 'capitalize',
              letterSpacing: '0.04em',
              fontSize: '0.7rem',
              borderColor: 'rgba(201, 162, 39, 0.35)',
              color: 'primary.light'
            }}
            variant='outlined'
          />
          {dateRange ? (
            <Typography
              component='span'
              variant='caption'
              sx={{
                alignSelf: 'center',
                color: 'text.secondary',
                letterSpacing: '0.06em',
                fontSize: '0.75rem'
              }}
            >
              {dateRange}
            </Typography>
          ) : null}
        </div>
        <h1 className='section-heading'>{project.title}</h1>
        <Typography className='section-lead' component='p'>
          {project.description}
        </Typography>
        <MaterialChips materials={project.materials} className='work-project-materials' />
      </header>

      <WorkProjectGallery images={project.images} projectTitle={project.title} />
    </PageShell>
  )
}

export default WorkProject
